
import React, { useEffect, useState } from 'react';
import { Form } from '@/components/ui/form';
import { Interview } from '@/types/interview';
import FormFields from './editor/FormFields';
import FormActions from './editor/FormActions';
import { useInterviewForm } from '../../hooks/interview/useInterviewForm';
import { toast } from 'sonner';
import { InterviewFormValues } from './editor/schemas/interviewFormSchema';

interface InterviewEditorProps {
  interview: Interview;
  onClose: () => void;
}

const InterviewEditor: React.FC<InterviewEditorProps> = ({ interview, onClose }) => {
  console.log('[FORCED REPLACEMENT] InterviewEditor - COMPLETELY NEW VERSION');
  // Force component to always re-mount with fresh state
  const [key] = useState(() => Date.now() + Math.random().toString(36).substr(2, 9));
  
  console.log('[InterviewEditor] Component mounted', { 
    interviewId: interview.id,
    hasPhotoUrl: !!interview.photoUrl,
    photoUrl: interview.photoUrl || 'No photo URL',
    hasContextUrl: !!interview.contextUrl,
    contextUrl: interview.contextUrl || 'No context URL'
  });
  
  const { form, onSubmit, isSaving } = useInterviewForm(interview, onClose);

  useEffect(() => {
    console.log('[InterviewEditor] Effect: Initializing with interview:', {
      id: interview.id,
      name: interview.intervieweeName,
      hasPhotoUrl: !!interview.photoUrl,
      photoUrl: interview.photoUrl || 'No photo URL',
      hasContextUrl: !!interview.contextUrl,
      contextUrl: interview.contextUrl || 'No context URL'
    });
    
    // Deep clone the interview object to ensure we're working with fresh data
    const interviewClone = JSON.parse(JSON.stringify(interview));
    
    // CRITICAL FIX: Clean tags before setting them in the form to prevent quote corruption
    let cleanedTags = [];
    if (Array.isArray(interviewClone.tags)) {
      cleanedTags = interviewClone.tags
        .filter(tag => tag !== null && tag !== undefined && typeof tag === 'string')
        .map(tag => {
          // Remove all quotes, brackets, and extra characters that may have been added by JSON processing
          let cleanTag = String(tag)
            .replace(/^["']|["']$/g, '') // Remove surrounding quotes
            .replace(/\\"/g, '') // Remove escaped quotes
            .replace(/["']/g, '') // Remove ANY remaining quotes
            .replace(/^#+/, '') // Remove any leading hashtags
            .replace(/[{}]/g, '') // Remove curly braces
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/[^\w\sæøåÆØÅ-]/g, '') // Remove special characters except Norwegian letters and hyphens
            .trim();
          return cleanTag;
        })
        .filter(tag => tag.length > 0 && tag !== '{}' && tag !== '"#{}"'); // Only keep valid tags
    }
    
    console.log('[InterviewEditor] Original tags:', interviewClone.tags, 'Cleaned tags:', cleanedTags);
    
    // Reset form with ALL fields to avoid any undefined fields
    // CRITICAL: Ensure context always has a value to prevent validation errors
    const contextValue = interviewClone.context || interviewClone.contextUrl 
      ? (interviewClone.context || `Kontekst: ${interviewClone.contextUrl}`)
      : 'Manuell kontekst';
    
    form.reset({
      title: interviewClone.jobTitle || '',
      intervieweeName: interviewClone.intervieweeName || '',
      company: interviewClone.company || '',
      context: contextValue,
      contextUrl: interviewClone.contextUrl || '',
      question: interviewClone.question || '',
      answer: interviewClone.answer || '',
      tags: cleanedTags, // Use cleaned tags instead of raw tags
      photoUrl: interviewClone.photoUrl || '',
      photographerCredit: interviewClone.photographerCredit || '',
      contextImageUrl: interviewClone.contextImageUrl || '',
      contextImageCaption: interviewClone.contextImageCaption || '',
      timestamp: interviewClone.timestamp, // Preserve original timestamp
    });
    
    // Specifically ensure photoUrl and contextUrl are properly initialized
    const fieldSettings = {
      shouldDirty: true,
      shouldTouch: true
    };
    
    form.setValue('photoUrl', interviewClone.photoUrl || '', fieldSettings);
    form.setValue('contextUrl', interviewClone.contextUrl || '', fieldSettings);
    
    // Preserve the original timestamp from the interview data
    if (interviewClone.timestamp) {
      form.setValue('timestamp', interviewClone.timestamp, fieldSettings);
    }
    
    console.log('[InterviewEditor] After form reset, form values:', form.getValues());
    
  }, [interview, form]);

  // Handle form submission with proper handling of photoUrl and contextUrl
  const handleSubmit = async (values: InterviewFormValues) => {
    try {
      console.log('[InterviewEditor] Form submitted with values:', {
        ...values,
        hasPhotoUrl: !!values.photoUrl,
        photoUrl: values.photoUrl || 'No photo URL',
        hasContextUrl: !!values.contextUrl,
        contextUrl: values.contextUrl || 'No context URL'
      });
      
      // Create a copy of the form values to avoid mutation
      const processedValues = { ...values };
      
      // Guard against saving blob URLs
      const photoUrl = processedValues.photoUrl;
      if (photoUrl?.startsWith('blob:')) {
        console.warn('[Editor] preventing save – blob URL present');
        toast.error('Cannot save: Invalid photo URL detected. Please re-upload the image.');
        return;
      }
      
      if (!processedValues.contextUrl) {
        // Use empty string as default
        processedValues.contextUrl = '';
      }
      
      // Don't override the user's selected timestamp
      // processedValues.timestamp already contains the user's selection
      
      console.log('[InterviewEditor] Final processed values for submission:', {
        ...processedValues,
        hasPhotoUrl: !!processedValues.photoUrl,
        photoUrl: processedValues.photoUrl || 'No photo URL',
        hasContextUrl: !!processedValues.contextUrl,
        contextUrl: processedValues.contextUrl || 'No context URL',
        timestamp: processedValues.timestamp
      });
      
      try {
        toast.loading('Lagrer intervju...', { id: 'saving-interview' });
        
        const result = await onSubmit(processedValues);
        
        if (result) {
          toast.success('Endringer lagret', { id: 'saving-interview' });
          // Close the form after successful save
          onClose();
        } else {
          toast.error('Kunne ikke lagre endringer', { id: 'saving-interview' });
        }
      } catch (submitError) {
        console.error('[InterviewEditor] Error in form submission:', submitError);
        toast.error('Kunne ikke lagre endringer. Vennligst prøv igjen.', { id: 'saving-interview' });
      }
    } catch (error) {
      console.error('[InterviewEditor] Error handling form submission:', error);
      toast.error('En feil oppstod. Vennligst prøv igjen.');
    }
  };

  return (
    <Form {...form}>
        <form 
          onSubmit={(e) => {
            console.log('[InterviewEditor] Form onSubmit event triggered');
            console.log('[InterviewEditor] Form validation errors:', form.formState.errors);
            form.handleSubmit(handleSubmit)(e);
          }} 
          className="space-y-6" 
          id="interview-form"
          key={key}
        >
          <FormFields 
            form={form} 
          />
          <FormActions 
            isSubmitting={isSaving || form.formState.isSubmitting} 
            onClose={onClose} 
          />
        </form>
      </Form>
  );
};

export default InterviewEditor;
